import { ApiService } from "../../../config";

export type CampoPrevia = { campo: string; rotulo: string; texto: string };
export type ItemManual = { texto: string; link: string | null };

export type PreviaDoEncerramento = {
  perfilNome: string;
  habilitado: boolean;
  postagens: number;
  respostas: number;
  servicos: number | null;
  categoriasAdicionais: number | null;
  categoriaFinal: string;
  categoriaFinalNome: string;
  temDescricao: boolean | null;
  camposRestaurados: CampoPrevia[];
  camposManuais: CampoPrevia[];
  camposSemHistorico: string[];
  whatsapp: boolean;
  minisite: boolean;
  instagram: number;
  contaGestora: string | null;
  resumo: string[];
  manuais: ItemManual[];
  encerramentoAtual: string | null;
  /** A nossa conta não gerencia mais a ficha: nada será apagado no Google. */
  semAcessoAFicha?: boolean;
  /** Quem está vendo pode marcar "tirar o nosso acesso" (Reinaldo, Thiago, Roberta, Ana Paula, Igor). */
  podeRemoverAcesso?: boolean;
  /** Quem está vendo aprova: começa na hora. Os outros só pedem. */
  encerraDireto?: boolean;
  aprovadores?: string;
};

export type TipoAndamento = {
  tipo: string;
  rotulo: string;
  total: number;
  feitos: number;
  erros: number;
  pulados: number;
  pendentes: number;
};

export type ItemAviso = { tipo: string; referencia: string | null; texto: string | null; link: string | null };

export type AndamentoDoEncerramento = {
  id: number;
  status: "PREVIA" | "AGUARDANDO_APROVACAO" | "EM_ANDAMENTO" | "CONCLUIDO" | "CANCELADO" | "FALHOU" | "RECUSADO";
  removerAcesso: boolean;
  solicitadoEm: string | null;
  iniciadoEm: string | null;
  concluidoEm: string | null;
  total: number;
  feitos: number;
  erros: number;
  pulados: number;
  pendentes: number;
  porTipo: TipoAndamento[];
  ultimosErros: ItemAviso[];
  avisos: ItemAviso[];
  solicitadoPor?: string | null;
  decididoPor?: string | null;
  decididoEm?: string | null;
  /** Quem está vendo pode aprovar ou recusar o pedido que aguarda. */
  podeDecidir?: boolean;
};

export type PedidoAguardando = {
  id: number;
  profilePublicId: string;
  perfilNome: string;
  solicitadoPor: string | null;
  solicitadoEm: string | null;
  removerAcesso: boolean;
};

type Envelope<T> = { message?: string; error?: boolean; data?: T };

const unwrap = <T>(response: { data?: Envelope<T> }): T => {
  const body = response?.data;
  if (!body || body.error) {
    throw new Error(body?.message || "Não foi possível concluir a operação.");
  }
  return body.data as T;
};

const base = (profileId: string) => `/business-profiles/${profileId}/encerramento`;

export const encerramentoApi = {
  async previa(profileId: string): Promise<PreviaDoEncerramento> {
    return unwrap(await ApiService.get(`${base(profileId)}/previa`));
  },

  async andamento(profileId: string): Promise<AndamentoDoEncerramento | null> {
    return unwrap(await ApiService.get(base(profileId)));
  },

  async iniciar(profileId: string, confirmacaoNome: string, removerAcesso: boolean): Promise<AndamentoDoEncerramento> {
    return unwrap(await ApiService.post(base(profileId), { confirmacaoNome, removerAcesso }));
  },

  async cancelar(profileId: string): Promise<AndamentoDoEncerramento> {
    return unwrap(await ApiService.post(`${base(profileId)}/cancelar`, {}));
  },

  async aprovar(profileId: string): Promise<AndamentoDoEncerramento> {
    return unwrap(await ApiService.post(`${base(profileId)}/aprovar`, {}));
  },

  async recusar(profileId: string): Promise<AndamentoDoEncerramento> {
    return unwrap(await ApiService.post(`${base(profileId)}/recusar`, {}));
  },

  /** Pedidos que esperam aprovação; vem vazio para quem não aprova. */
  async aguardando(): Promise<PedidoAguardando[]> {
    return unwrap<PedidoAguardando[]>(await ApiService.get("/business-profiles/encerramentos/aguardando")) ?? [];
  },
};

/** Mesmo critério do backend: ignora maiúsculas e espaços. */
export const nomeConfere = (digitado: string, nome: string): boolean => {
  const limpar = (valor: string) => valor.replace(/\s+/g, "").toLowerCase();
  const a = limpar(digitado || "");
  return a.length > 0 && a === limpar(nome || "");
};
